import { useEffect } from 'react';

import { useAppDispatch } from 'dux/app/hooks';

import { styled } from '@prose-ui/legacy';
import { useOnKeydownListener } from 'hooks/useOnKeydownListener';

import { Image } from 'Components/Image';
import { Typography } from 'Components/Typography';
import YoutubeVideo from 'Components/YoutubeVideo';

import closeIcon from 'assets/images/icon-close-white.svg';

import { trackHeapEvent } from 'dux/tracking/actions';

const Modal = styled.div<{ isVideoOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  outline: 0;
  padding: 0;

  display: ${({ isVideoOpen }) => (isVideoOpen ? 'block' : 'none')};
`;

const CloseButton = styled(Typography)`
  position: fixed;
  top: 60px;
  right: 28px;
  color: white;
  cursor: pointer;
`;

type YoutubeVideoModalProps = {
  onClose: () => void;
  isVideoOpen: boolean;
  videoId: string;
  name: string;
};

export const YoutubeVideoModal = ({
  onClose,
  isVideoOpen,
  videoId,
  name,
}: YoutubeVideoModalProps) => {
  const dispatch = useAppDispatch();
  useOnKeydownListener({ onKeyDown: onClose, key: 'Escape' });

  useEffect(() => {
    if (isVideoOpen) {
      dispatch(trackHeapEvent('YouTube iframe open'));
    }
  }, [isVideoOpen]);

  const handleClose = () => {
    dispatch(trackHeapEvent('YouTube iframe close'));
    onClose();
  };
  return (
    <Modal data-testid={`${name}-video-modal`} isVideoOpen={isVideoOpen}>
      <CloseButton
        data-click="exit"
        data-from="youtube-iframe-modal"
        data-testid={`${name}-youtube-iframe-close-button`}
        onClick={handleClose}
      >
        <Image alt="Close youtube video button" height={16} src={closeIcon} width={16} />
      </CloseButton>

      <YoutubeVideo
        events={{
          onPlay: () => dispatch(trackHeapEvent('Video play')),
          onPause: () => dispatch(trackHeapEvent('Video paused')),
          onEnd: () => dispatch(trackHeapEvent('Video ended')),
        }}
        isAutoplay
        isOpen={isVideoOpen}
        onClose={handleClose}
        videoId={videoId}
      />
    </Modal>
  );
};
